import React, { useMemo } from 'react';

/**
 * LivingOrb
 *
 * The signature visual of PCOS Insights. A 5-layer biological system:
 *
 *   1. Outer atmosphere        (slowest breath, largest blur)
 *   2. Middle diffusion        (holds the orb in phase-tinted light)
 *   3. Inner illumination      (pearl warm gradient, off-center highlight)
 *   4. Glass shell + 7 rings   (SVG, phase-tinted, hormone nodes on rings)
 *   5. 3-depth particle system (near / mid / far)
 *
 * The orb is fully driven by CSS variables scoped to the parent
 * [data-phase] wrapper. It contains no phase logic itself.
 *
 * Props:
 *   tag         string   — small caps eyebrow ("Cycle Day")
 *   day         number   — the big Fraunces day number
 *   of          string   — trailing " / 28" or "" for waiting
 *   phaseName   string   — "Luteal Phase" (uppercase, tracked)
 *   sentence    string   — one calm sentence about today's body
 */
export default function LivingOrb({ tag, day, of, phaseName, sentence }) {
  // Seed mid + far particles once per mount. Positions/timings are random
  // so no two sessions of the orb ever feel identical.
  const midParticles = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const angle = Math.random() * Math.PI * 2;
    const r = 46 + Math.random() * 8;
    return {
      i,
      left: 50 + Math.cos(angle) * r,
      top:  50 + Math.sin(angle) * r,
      size: 1.5 + Math.random() * 1.5,
      md:   (9 + Math.random() * 6).toFixed(2),
      mdl:  (-Math.random() * 12).toFixed(2),
      mx:   (Math.random() * 12 - 6).toFixed(1),
      my:   (-6 - Math.random() * 8).toFixed(1),
      mx2:  (Math.random() * 8 - 4).toFixed(1),
      my2:  (-14 - Math.random() * 10).toFixed(1),
      color: ['var(--cx-hormone-1)', 'var(--cx-hormone-2)', 'var(--cx-hormone-3)', 'var(--cx-aura-a)'][i % 4],
    };
  }), []);

  const farParticles = useMemo(() => Array.from({ length: 22 }, (_, i) => ({
    i,
    left: Math.random() * 100,
    top:  Math.random() * 100,
    md:   (16 + Math.random() * 12).toFixed(2),
    mdl:  (-Math.random() * 24).toFixed(2),
    mx:   (Math.random() * 6 - 3).toFixed(1),
    my:   (-18 - Math.random() * 12).toFixed(1),
  })), []);

  return (
    <div className="cx-orb-stage" data-testid="cycle-living-orb">
      <div className="cx-orb-wrap">
        {/* Layers 1-3 */}
        <div className="cx-layer-atm"  aria-hidden="true"/>
        <div className="cx-layer-diff" aria-hidden="true"/>
        <div className="cx-layer-ill"  aria-hidden="true"/>

        {/* Layer 4 — glass shell + 7 rings + reflections */}
        <svg className="cx-orb-svg" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <radialGradient id="cxShell" cx="40%" cy="34%" r="70%">
              <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="1"/>
              <stop offset="34%"  stopColor="#FFFFFF" stopOpacity=".72"/>
              <stop offset="72%"  stopColor="var(--cx-aura-b)" stopOpacity=".32"/>
              <stop offset="100%" stopColor="var(--cx-aura-a)" stopOpacity=".14"/>
            </radialGradient>
            <radialGradient id="cxPearl" cx="46%" cy="40%" r="26%">
              <stop offset="0%"   stopColor="#FFFFFF" stopOpacity=".92"/>
              <stop offset="50%"  stopColor="var(--cx-pearl-warm)" stopOpacity=".32"/>
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0"/>
            </radialGradient>
            <linearGradient id="cxRim" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="var(--cx-aura-a)" stopOpacity=".78"/>
              <stop offset="45%"  stopColor="#FFFFFF"          stopOpacity=".9"/>
              <stop offset="100%" stopColor="var(--cx-aura-a)" stopOpacity=".78"/>
            </linearGradient>
            <filter id="cxSoft"><feGaussianBlur stdDeviation="0.6"/></filter>
            <filter id="cxSofter"><feGaussianBlur stdDeviation="1.4"/></filter>
          </defs>

          {/* 7 orbital rings */}
          <g className="cx-ring cx-r-1">
            <circle cx="250" cy="250" r="245" fill="none"
              stroke="var(--cx-aura-a)" strokeWidth=".35" opacity=".2"
              strokeDasharray="1 6"/>
          </g>
          <g className="cx-ring cx-r-2">
            <circle cx="250" cy="250" r="232" fill="none"
              stroke="var(--cx-aura-a)" strokeWidth=".45" opacity=".26"/>
            <circle cx="250" cy="18"  r="2.4" fill="var(--cx-hormone-1)" opacity=".85" filter="url(#cxSoft)"/>
            <circle cx="482" cy="192" r="1.6" fill="var(--cx-hormone-2)" opacity=".65"/>
          </g>
          <g className="cx-ring cx-r-3">
            <circle cx="250" cy="250" r="220" fill="none"
              stroke="var(--cx-aura-a)" strokeWidth=".5" opacity=".34"/>
            <circle cx="46"  cy="290" r="1.8" fill="var(--cx-aura-a)" opacity=".78"/>
            <circle cx="446" cy="128" r="2.2" fill="var(--cx-hormone-1)" opacity=".75" filter="url(#cxSoft)"/>
            <circle cx="322" cy="454" r="1.4" fill="var(--cx-aura-a)" opacity=".65"/>
          </g>
          <g className="cx-ring cx-r-4">
            <circle cx="250" cy="250" r="208" fill="none"
              stroke="var(--cx-aura-a)" strokeWidth=".4" opacity=".38"
              filter="url(#cxSoft)"/>
            <circle cx="250" cy="42"  r="1.4" fill="#FFFFFF" opacity=".85"/>
            <circle cx="422" cy="352" r="1.2" fill="#FFFFFF" opacity=".7"/>
          </g>

          {/* Glass shell + pearl core */}
          <circle cx="250" cy="250" r="198" fill="url(#cxShell)"/>
          <circle cx="250" cy="250" r="198" fill="url(#cxPearl)" opacity=".85"/>

          {/* Rim */}
          <circle cx="250" cy="250" r="198" fill="none"
            stroke="url(#cxRim)" strokeWidth="1.3" opacity=".9"/>
          <circle cx="250" cy="250" r="206" fill="none"
            stroke="var(--cx-aura-a)" strokeWidth=".35" opacity=".25" filter="url(#cxSoft)"/>
          <circle cx="250" cy="250" r="192" fill="none"
            stroke="#FFFFFF" strokeWidth=".35" opacity=".28"/>

          <g className="cx-ring cx-r-5">
            <circle cx="250" cy="250" r="186" fill="none"
              stroke="#FFFFFF" strokeWidth=".65" opacity=".38"/>
            <circle cx="250" cy="64"  r="1.4" fill="#FFFFFF" opacity=".92"/>
            <circle cx="408" cy="342" r="1"   fill="#FFFFFF" opacity=".65"/>
          </g>
          <g className="cx-ring cx-r-6">
            <circle cx="250" cy="250" r="164" fill="none"
              stroke="#FFFFFF" strokeWidth=".35" opacity=".22"/>
            <circle cx="84"  cy="250" r="1.1" fill="#FFFFFF" opacity=".78"/>
          </g>
          <g className="cx-ring cx-r-7">
            <circle cx="250" cy="250" r="138" fill="none"
              stroke="#FFFFFF" strokeWidth=".28" opacity=".18"/>
            <circle cx="250" cy="112" r=".9" fill="#FFFFFF" opacity=".85"/>
          </g>

          {/* Reflections */}
          <path d="M 82 240 A 166 166 0 0 1 240 82"
            fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" opacity=".82"/>
          <path d="M 118 240 A 130 130 0 0 1 240 118"
            fill="none" stroke="#FFFFFF" strokeWidth=".7" strokeLinecap="round" opacity=".58"/>
          <path d="M 164 232 A 82 82 0 0 1 232 164"
            fill="none" stroke="#FFFFFF" strokeWidth=".35" strokeLinecap="round" opacity=".55"/>
          <ellipse cx="212" cy="120" rx="46" ry="18" fill="#FFFFFF" opacity=".32" filter="url(#cxSofter)"/>
          <path d="M 412 302 A 158 158 0 0 1 316 414"
            fill="none" stroke="var(--cx-aura-b)" strokeWidth="1.6" opacity=".38" filter="url(#cxSoft)"/>
          <path d="M 416 260 A 168 168 0 0 1 372 388"
            fill="none" stroke="rgba(14,15,19,.045)" strokeWidth="2" opacity=".7"/>
        </svg>

        {/* Near particles — hand-placed, 3 big soft bright */}
        <div className="cx-particles-near" aria-hidden="true">
          <span className="cx-pn" style={{ top: '2%', left: '52%', width: 5, height: 5,
            '--md':'14.2s','--mdl':'-1s','--mx':'8px','--my':'-10px','--mx2':'-4px','--my2':'-18px',
            '--m-color':'var(--cx-hormone-1)' }}/>
          <span className="cx-pn" style={{ bottom: '8%', left: '5%', width: 5, height: 5,
            '--md':'16.6s','--mdl':'-5s','--mx':'6px','--my':'-8px','--mx2':'2px','--my2':'-14px',
            '--m-color':'var(--cx-hormone-3)' }}/>
          <span className="cx-pn" style={{ top: '38%', right: '1%', width: 4, height: 4,
            '--md':'15.1s','--mdl':'-9s','--mx':'-6px','--my':'6px','--mx2':'-2px','--my2':'12px',
            '--m-color':'var(--cx-hormone-2)' }}/>
        </div>

        {/* Mid particles — seeded once per mount */}
        <div className="cx-particles-mid" aria-hidden="true">
          {midParticles.map(p => (
            <span key={p.i} className="cx-pm" style={{
              left: p.left + '%', top: p.top + '%', width: p.size, height: p.size,
              '--md': p.md + 's', '--mdl': p.mdl + 's',
              '--mx': p.mx + 'px', '--my': p.my + 'px',
              '--mx2': p.mx2 + 'px', '--my2': p.my2 + 'px',
              '--m-color': p.color,
            }}/>
          ))}
        </div>

        {/* Far particles */}
        <div className="cx-particles-far" aria-hidden="true">
          {farParticles.map(p => (
            <span key={p.i} className="cx-pf" style={{
              left: p.left + '%', top: p.top + '%',
              '--md': p.md + 's', '--mdl': p.mdl + 's',
              '--mx': p.mx + 'px', '--my': p.my + 'px',
            }}/>
          ))}
        </div>

        {/* Copy — the one thing the user reads */}
        <div className="cx-orb-content">
          <div className="cx-tag">{tag}</div>
          <div className="cx-day">
            <span>{day}</span>
            {of && <span className="cx-of">{of}</span>}
          </div>
          <div className="cx-phase-name">{phaseName}</div>
          <p className="cx-sentence">{sentence}</p>
        </div>
      </div>
    </div>
  );
}
